import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../../core/components/toss';
import { getRankingService } from '../../../domains/ranking';
import styles from './TossRanking.module.css';

const CATEGORY_FILTERS = ['전체', '영양제', '일반의약품'];
const PAGE_LIMIT = 20;

const TossRanking = () => {
  const [catFilter, setCatFilter] = useState('전체');
  const [rankings, setRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchRankings = useCallback(async (filter, pageNum = 1) => {
    setIsLoading(true);
    setError('');

    try {
      const service = getRankingService();

      if (filter === '영양제') {
        const res = await service.getSupplementsRanking({ page: pageNum, limit: PAGE_LIMIT });
        if (res?.result_code === '200') {
          const items = (res.list || []).map(s => ({
            rank: s.rank,
            id: s.id,
            name: s.name,
            brand: s.manufacturer || '-',
            cat: '영양제',
            price: s.price || 0,
            thumbnail: s.thumbnail_url,
            category: s.category1 || '영양제',
          }));
          setRankings(prev => pageNum === 1 ? items : [...prev, ...items]);
          setHasNext(res.has_next || false);
          setTotalCount(res.total_count || 0);
        }
      } else if (filter === '일반의약품') {
        const res = await service.getMedicationsRanking({ page: pageNum, limit: PAGE_LIMIT });
        if (res?.result_code === '200') {
          const items = (res.list || []).map(m => ({
            rank: m.rank,
            id: m.id,
            name: m.item_name,
            brand: m.entp_name || '-',
            cat: '일반의약품',
            price: 0,
            thumbnail: m.item_image,
            category: '일반의약품',
          }));
          setRankings(prev => pageNum === 1 ? items : [...prev, ...items]);
          setHasNext(res.has_next || false);
          setTotalCount(res.total_count || 0);
        }
      } else {
        // 전체: 두 API를 동시에 호출
        const [supRes, medRes] = await Promise.all([
          service.getSupplementsRanking({ page: pageNum, limit: PAGE_LIMIT }),
          service.getMedicationsRanking({ page: pageNum, limit: PAGE_LIMIT }),
        ]);

        const supItems = (supRes?.result_code === '200' ? supRes.list || [] : []).map(s => ({
          rank: s.rank,
          id: `sup-${s.id}`,
          name: s.name,
          brand: s.manufacturer || '-',
          cat: '영양제',
          price: s.price || 0,
          thumbnail: s.thumbnail_url,
          category: s.category1 || '영양제',
        }));

        const medItems = (medRes?.result_code === '200' ? medRes.list || [] : []).map(m => ({
          rank: m.rank,
          id: `med-${m.id}`,
          name: m.item_name,
          brand: m.entp_name || '-',
          cat: '일반의약품',
          price: 0,
          thumbnail: m.item_image,
          category: '일반의약품',
        }));

        const combined = [...supItems, ...medItems].sort((a, b) => a.rank - b.rank);
        combined.forEach((item, i) => { item.rank = (pageNum - 1) * PAGE_LIMIT * 2 + i + 1; });

        setRankings(prev => pageNum === 1 ? combined : [...prev, ...combined]);
        setHasNext(
          (supRes?.has_next || false) || (medRes?.has_next || false)
        );
        setTotalCount(
          (supRes?.total_count || 0) + (medRes?.total_count || 0)
        );
      }
    } catch (e) {
      console.error('랭킹 조회 실패:', e);
      setError('랭킹 데이터를 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchRankings(catFilter, 1);
  }, [catFilter, fetchRankings]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchRankings(catFilter, nextPage);
  };

  const handleTabChange = (filter) => {
    setCatFilter(filter);
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sectionCard}>
        <div className={styles.cardHeader}>
          <span className={styles.tableTitle}>전국 판매 순위</span>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.filterTabs}>
            {CATEGORY_FILTERS.map(item => (
              <button
                key={item}
                className={`${styles.filterTab} ${catFilter === item ? styles.filterTabActive : ''}`}
                onClick={() => handleTabChange(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.tableBody}>
          {isLoading && rankings.length === 0 ? (
            <div className={styles.loadingWrap}>
              <Loader2 size={20} className={styles.spinner} />
              데이터를 불러오는 중...
            </div>
          ) : error ? (
            <div className={styles.errorWrap}>
              <span className={styles.errorText}>{error}</span>
              <button className={styles.retryBtn} onClick={() => fetchRankings(catFilter, 1)}>
                다시 시도
              </button>
            </div>
          ) : rankings.length === 0 ? (
            <div className={styles.emptyState}>랭킹 데이터가 없습니다.</div>
          ) : (
            <>
              <table className={styles.rankTable}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center', width: 56 }}>순위</th>
                    <th style={{ textAlign: 'left' }}>제품명</th>
                    <th style={{ textAlign: 'center', width: 80 }}>카테고리</th>
                    <th style={{ textAlign: 'right', width: 100 }}>판매가</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((r) => (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`${styles.rankBadge} ${r.rank <= 3 ? styles.rankTop : styles.rankNormal}`}>
                          {r.rank}
                        </span>
                      </td>
                      <td>
                        <div className={styles.productCell}>
                          {r.thumbnail && (
                            <img
                              src={r.thumbnail}
                              alt={r.name}
                              className={styles.productImg}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <div className={styles.productName}>{r.name}</div>
                            <div className={styles.productMeta}>{r.brand}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Badge
                          color={r.cat === '영양제' ? '#0064FF' : '#8B5CF6'}
                          bg={r.cat === '영양제' ? '#E1EFFF' : '#F3EAFF'}
                        >
                          {r.cat}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'right' }} className={styles.price}>
                        {r.price > 0 ? `₩${r.price.toLocaleString()}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasNext && (
                <div className={styles.loadMoreWrap}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                    disabled={isLoading}
                  >
                    {isLoading ? '불러오는 중...' : '더 보기'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div className={styles.cardFooter}>
          <span className={styles.footerCount}>총 {totalCount > 0 ? totalCount.toLocaleString() : rankings.length}개 제품</span>
          <span className={styles.footerSub}>카테고리: {catFilter}</span>
        </div>
      </div>
    </div>
  );
};

export default TossRanking;
